// src/pages/letters/Print.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../core/api';
import './Print.css';

const API_BASE = api.defaults.baseURL || '';
const imgUrl = (p) => {
  if (!p) return null;
  return /^(https?:|blob:|data:)/.test(p) ? p : `${API_BASE}${p}`;
};

const FA = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (v) =>
  v === undefined || v === null ? '' : String(v).replace(/\d/g, (d) => FA[+d]);

const COUNT_WORDS = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده'];
const attachText = (n) => (n > 0 ? `${COUNT_WORDS[n] || toFa(n)} برگ` : 'ندارد');
// const attachText = (n) => (n > 0 ?'دارد' : 'ندارد');

/* حاشیه‌ی چاپگر صفر؛ حاشیه‌ی نامه را خود برگه می‌دهد */
const PAGE_CSS = `
@page { size: A4 portrait; margin: 0; }
@media print {
  html, body { width: 210mm; background: none !important; margin: 0 !important; padding: 0 !important; }
}
`;

const MM = 96 / 25.4;
const CAP = (296 - 16 - 30) * MM;

export default function LetterPrint() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [letter, setLetter] = useState(null);
  const [seller, setSeller] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [pages, setPages] = useState(null); // [{ start, height }]

  const measureRef = useRef(null);

  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-lt-page', '');
    el.textContent = PAGE_CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

useEffect(() => {
  if (letter) document.title = `نامه ${letter.serial} | پنل فاکتور`;
}, [letter]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lRes, cRes] = await Promise.all([
          api.get(`/letters/${id}`),
          api.get('/company').catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const l = lRes.data?.letter;
        if (!l) {
          setNotFound(true);
          return;
        }
        setLetter(l);
        setSeller(cRes.data?.company || {});
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError('خطا در دریافت اطلاعات نامه');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* متن‌های بدون تگ را داخل <p> می‌گذاریم تا همه‌چیز بلوک باشد */
  const bodyHtml = useMemo(() => {
    if (!letter?.body) return '';
    const box = document.createElement('div');
    box.innerHTML = letter.body;
    let out = '';
    box.childNodes.forEach((n) => {
      if (n.nodeType === 1) out += n.outerHTML;
      else if (n.nodeType === 3 && n.textContent.trim()) {
        const p = document.createElement('p');
        p.textContent = n.textContent;
        out += p.outerHTML;
      }
    });
    return out;
  }, [letter]);

  /* ============== اندازه‌گیری و تعیین نقطه‌های شکست ============== */
  useEffect(() => {
    if (!letter || pages) return undefined;
    let cancelled = false;

    (async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      const m = measureRef.current;
      if (!m) return;

      const pending = [...m.querySelectorAll('img')].filter((i) => !i.complete);
      await Promise.all(
        pending.map(
          (i) =>
            new Promise((res) => {
              i.onload = res;
              i.onerror = res;
            })
        )
      );
      if (cancelled) return;

      const flow = m.firstElementChild;
      if (!flow) return;
      const base = flow.getBoundingClientRect().top;
      const total = flow.getBoundingClientRect().height;

      const bodyEl = flow.querySelector('.lt-body');
      const lhRaw = bodyEl ? parseFloat(getComputedStyle(bodyEl).lineHeight) : NaN;
      const lh = Number.isFinite(lhRaw) && lhRaw > 0 ? lhRaw : 26;
      const lineStep = Math.max(1, Math.floor(CAP / lh)) * lh;

      /* اتم‌ها: سربرگ، گیرنده، هر بلوک متن (هر li جدا)، بخش امضا */
      const atoms = [];
      const add = (el) => {
        const r = el.getBoundingClientRect();
        if (r.height <= 0) return;
        atoms.push({ top: r.top - base, bottom: r.bottom - base });
      };
      [...flow.children].forEach((c) => {
        if (c.classList.contains('lt-body')) {
          [...c.children].forEach((b) => {
            if (b.tagName === 'UL' || b.tagName === 'OL') [...b.children].forEach(add);
            else add(b);
          });
        } else add(c);
      });

      const starts = [0];
      let start = 0;
      atoms.forEach((a) => {
        if (a.bottom - start <= CAP) return;
        if (a.top > start + 1) {
          start = a.top;
          starts.push(start);
        }
        while (a.bottom - start > CAP + 1) {
          start += lineStep;
          starts.push(start);
        }
      });

      const result = starts.map((s, i) => {
        const end = i < starts.length - 1 ? starts[i + 1] : total;
        return { start: s, height: Math.min(Math.max(end - s, 1), CAP) };
      });

      if (!cancelled) setPages(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [letter, pages, bodyHtml]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else {
      window.close();
      setTimeout(() => navigate('/dashboard/letters'), 150);
    }
  };

  if (loading || notFound || error || !letter) {
    return (
      <div className="lt-wrap">
        <div className="lt-status">
          {loading && 'در حال بارگذاری نامه...'}
          {notFound && 'نامه یافت نشد'}
          {error}
        </div>
      </div>
    );
  }

  const logoUrl = imgUrl(seller.logo);

  const copies = letter.copies || [];
const attachments = letter.attachments || [];
const totalPages = (pages ? pages.length : 0) + attachments.length;
  const hasSigner = letter.signerName || letter.signerPosition;
  const hasTo = letter.recipientName || letter.recipientPosition;

  /* کل نامه به‌صورت یک ستون پیوسته */
  const flow = (
    <div className="lt-flow">
      <div className="lt-head">
        <div className="lt-logo-box">
          {logoUrl && <img src={logoUrl} alt="لوگو" className="lt-logo" />}
        </div>
        <div className="lt-title">{seller.name || ''}</div>
        <div className="lt-meta">
          <div>
            <span>شماره نامه: </span>
            <b>{toFa(letter.serial)}</b>
          </div>
          <div>
            <span>تاریخ: </span>
            <b>{toFa(letter.date)}</b>
          </div>
<div>
                <span>پیوست: </span>
                <b>{attachText(attachments.length)}</b>
              </div>

        </div>
      </div>


          <div className="lt-besm">بسمه تعالی</div>


      {hasTo && (
        <div className="lt-to">
          {letter.recipientName && <div className="lt-to-name">{letter.recipientName}</div>}
          {letter.recipientPosition && (
            <div className="lt-to-pos">{letter.recipientPosition}</div>
          )}
        </div>
      )}

      <div className="lt-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />

      <div className="lt-tail">
        <div className="lt-signers">
          <div className="lt-signer">
            {letter.signerName && <div className="lt-signer-name">{letter.signerName}</div>}
            {letter.signerPosition && (
              <div className="lt-signer-pos">{letter.signerPosition}</div>
            )}
            {!hasSigner && <div className="lt-signer-pos">محل امضا</div>}
          
          </div>
        </div>


   
      </div>
    </div>
  );

  /* ستون‌های جدول پیوست‌ها */
  /* پیوست‌ها همه در یک برگه‌اند؛ اگر پیوست باشد یک صفحه به کل اضافه می‌شود */
  return (
    <div className="lt-wrap">
      {/* ============ نوار دکمه‌ها (چاپ نمی‌شود) ============ */}
      <div className="lt-actions">
      
     
        <button
          type="button"
          className="lt-btn print"
          onClick={() => window.print()}
          disabled={!pages}
        >
          <i className="fa-solid fa-print" /> چاپ
        </button>
        <button type="button" className="lt-btn back" onClick={goBack}>
          <i className="fa-solid fa-arrow-right" /> بازگشت
        </button>
      </div>

      {/* لایه‌ی اندازه‌گیری (نامرئی، فقط تا قبل از محاسبه) */}
      {!pages && (
        <>
          <div className="lt-measure" ref={measureRef} aria-hidden="true">
            {flow}
          </div>
          <div className="lt-status">در حال آماده‌سازی صفحات...</div>
        </>
      )}

{pages && (
  <div className="lt-sheets">
    {/* ============ صفحه‌های نامه ============ */}
    {pages.map((pg, p) => {
      const isLast = p === pages.length - 1;
      return (
        <div className="lt-page" key={`p${p}`}>
          <div className="lt-win" style={{ height: pg.height }}>
            <div style={{ marginTop: -pg.start }}>{flow}</div>
          </div>
          {isLast && <CopiesBlock copies={copies} />}
          <PageFooter seller={seller} />
          <div className="lt-pn">
            صفحه {(p + 1).toLocaleString('fa-IR')} از {totalPages.toLocaleString('fa-IR')}
          </div>
        </div>
      );
    })}

    {/* ============ هر پیوست یک صفحه ============ */}
    {attachments.map((u, i) => (
      <div className="lt-att-page" key={u}>
        <div className="lt-att-cell">
          <img src={imgUrl(u)} alt={`پیوست ${i + 1}`} />
        </div>
        <PageFooter seller={seller} />
        <div className="lt-pn">
          صفحه {(pages.length + i + 1).toLocaleString('fa-IR')} از{' '}
          {totalPages.toLocaleString('fa-IR')}
        </div>
      </div>
    ))}
  </div>
)}
    </div>
  );
}

function PageFooter({ seller }) {
  const parts = [
    seller.email && (
      <span key="e">ایمیل: <bdi dir="ltr">{seller.email}</bdi></span>
    ),
    seller.website && (
      <span key="w">وب‌سایت: <bdi dir="ltr">{seller.website}</bdi></span>
    ),
    seller.landline && (
      <span key="l">تلفن ثابت: <bdi dir="ltr">{toFa(seller.landline)}</bdi></span>
    ),
  ].filter(Boolean);

  if (!seller.address && parts.length === 0) return null;

  return (
    <div className="lt-footer">
      {seller.address && <div>آدرس: {seller.address}</div>}
      {parts.length > 0 && (
        <div className="lt-footer-row">
          {parts.flatMap((el, i) =>
            i ? [<span key={`s${i}`} className="lt-footer-sep">|</span>, el] : [el]
          )}
        </div>
      )}
    </div>
  );
}

function CopiesBlock({ copies }) {
  if (!copies || copies.length === 0) return null;
  return (
    <div className="lt-copies lt-copies-bottom">
      <span className="lt-copies-label">رونوشت:</span>
      <ul className="lt-copies-list">
        {copies.map((c, i) => (
          <li key={i}>
            {c.name}
            {c.position ? ` - ${c.position}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}